import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, UserRound } from 'lucide-react';
import api from '../../../services/api';
import { AnchoredMenu } from '../../../components/ui/AnchoredMenu';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';
import type { Email, EmailWorkflowStatus } from '../useData';
import { emailPartitionAtSearch } from '../utils/emailPartitionAtQuery';
import {
  WORKFLOW_STATUS_CHIP,
  assigneeDisplayName as resolveAssigneeName,
  assigneeInitial,
} from '../utils/workflowDisplay';

interface UserOption {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
  userEmail: string;
}

interface EmailWorkflowControlsProps {
  email: Email;
  onEmailUpdate?: (updated?: Email) => void;
  className?: string;
}

const STATUS_OPTIONS: { value: EmailWorkflowStatus; labelKey: string }[] = [
  { value: 'open', labelKey: 'statusOpen' },
  { value: 'working', labelKey: 'statusWorking' },
  { value: 'closed', labelKey: 'statusClosed' },
];

function userLabel(u: UserOption): string {
  return (
    u.userDisplayName?.trim() ||
    [u.userFirstName, u.userLastName].filter(Boolean).join(' ').trim() ||
    u.userEmail
  );
}

function resolveStatusValue(
  workflowStatus: Email['workflowStatus'],
  assignedToUserId: Email['assignedToUserId'],
): string {
  if (assignedToUserId == null) return '';
  return workflowStatus ?? 'open';
}

function AssigneeAvatar({ name, unassigned }: { name: string | null; unassigned?: boolean }) {
  if (unassigned || !name) {
    return (
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
          'bg-slate-100 text-slate-500 ring-1 ring-slate-200/80',
          'dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700',
        )}
        aria-hidden
      >
        <UserRound className="h-3.5 w-3.5" strokeWidth={2} />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold',
        'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200/80',
        'dark:bg-indigo-950/60 dark:text-indigo-200 dark:ring-indigo-800',
      )}
      aria-hidden
    >
      {assigneeInitial(name)}
    </span>
  );
}

const EmailWorkflowControls: React.FC<EmailWorkflowControlsProps> = ({ email, onEmailUpdate, className }) => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation('emails');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [assigneeId, setAssigneeId] = useState<string>(
    email.assignedToUserId != null ? String(email.assignedToUserId) : '',
  );
  const [status, setStatus] = useState<string>(
    resolveStatusValue(email.workflowStatus, email.assignedToUserId),
  );
  const [saving, setSaving] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const assigneeAnchorRef = useRef<HTMLButtonElement>(null);
  const partitionQuery = emailPartitionAtSearch(email.partitionAt);
  const hasAssignee = assigneeId !== '';

  useEffect(() => {
    setAssigneeId(email.assignedToUserId != null ? String(email.assignedToUserId) : '');
    setStatus(resolveStatusValue(email.workflowStatus, email.assignedToUserId));
  }, [email.id, email.assignedToUserId, email.workflowStatus, email.partitionAt]);

  const canAssignToOthers =
    user?.role === 'ADMIN' || user?.canAssignEmailsToOthers === true;

  useEffect(() => {
    if (!canAssignToOthers) return;
    api
      .get<UserOption[]>('/emails/assignable-users')
      .then((res) => {
        setUsers(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => setUsers([]));
  }, [canAssignToOthers]);

  const assigneeName = useMemo(() => {
    const fromEmail = resolveAssigneeName(email);
    if (fromEmail) return fromEmail;
    if (assigneeId) {
      const match = users.find((u) => String(u.id) === assigneeId);
      if (match) return userLabel(match);
    }
    return null;
  }, [assigneeId, email, users]);

  const activeStatus = (status || 'open') as EmailWorkflowStatus;
  const statusChip = hasAssignee ? WORKFLOW_STATUS_CHIP[activeStatus] : null;

  const filteredUsers = useMemo(() => {
    const q = assigneeSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const label = userLabel(u).toLowerCase();
      return label.includes(q) || u.userEmail.toLowerCase().includes(q);
    });
  }, [users, assigneeSearch]);

  const patchAssign = async (value: string) => {
    if (!email.id) return;
    setAssigneeOpen(false);
    setAssigneeSearch('');
    setSaving(true);
    try {
      const assignedToUserId = value ? Number(value) : null;
      const { data } = await api.patch(`/emails/${email.id}/assign${partitionQuery}`, {
        assignedToUserId,
      });
      setAssigneeId(value);
      setStatus(resolveStatusValue(data.workflowStatus, data.assignedToUserId));
      onEmailUpdate?.(data);
    } catch (err) {
      console.error('Failed to assign email', err);
    } finally {
      setSaving(false);
    }
  };

  const patchStatus = async (value: EmailWorkflowStatus) => {
    if (!email.id || !hasAssignee) return;
    if (value === status) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/emails/${email.id}/status${partitionQuery}`, {
        workflowStatus: value,
      });
      setStatus(value);
      onEmailUpdate?.(data);
    } catch (err) {
      console.error('Failed to update status', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={cn(
        'flex w-full min-w-0 max-w-full flex-wrap items-stretch rounded-lg border',
        colors.border,
        colors.surface,
        saving && 'opacity-80',
        className,
      )}
    >
      {/* Assignee */}
      <div className={cn('flex min-w-0 items-center gap-1.5 border-r px-2 py-1', colors.border)}>
        <span className={cn('hidden sm:inline text-[10px] font-semibold uppercase tracking-wider shrink-0', colors.textMuted)}>
          {t('assignee', { defaultValue: 'Assignee' })}
        </span>

        {canAssignToOthers ? (
        <button
          ref={assigneeAnchorRef}
          type="button"
          disabled={saving}
          onClick={() => setAssigneeOpen((open) => !open)}
          className={cn(
            'inline-flex min-w-0 max-w-[min(100%,12rem)] items-center gap-2 rounded-lg px-1.5 py-1 transition-colors',
            'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
            'disabled:cursor-not-allowed',
          )}
          aria-expanded={assigneeOpen}
          aria-haspopup="listbox"
        >
          <AssigneeAvatar name={assigneeName} unassigned={!hasAssignee} />
          <span className={cn('min-w-0 truncate text-left text-sm font-medium', hasAssignee ? colors.text : colors.textMuted)}>
            {assigneeName ?? t('unassigned')}
          </span>
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 shrink-0 transition-transform',
              colors.textMuted,
              assigneeOpen && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
        ) : (
        <div className="inline-flex min-w-0 max-w-[min(100%,12rem)] items-center gap-2 px-1.5 py-1">
          <AssigneeAvatar name={assigneeName} unassigned={!hasAssignee} />
          <span className={cn('min-w-0 truncate text-left text-sm font-medium', hasAssignee ? colors.text : colors.textMuted)}>
            {assigneeName ?? t('unassigned')}
          </span>
        </div>
        )}

        {canAssignToOthers && (
        <AnchoredMenu
          open={assigneeOpen}
          onClose={() => {
            setAssigneeOpen(false);
            setAssigneeSearch('');
          }}
          anchorRef={assigneeAnchorRef}
        >
          <div
            className={cn(
              'w-[min(18rem,calc(100vw-2rem))] overflow-hidden rounded-xl border shadow-lg',
              colors.border,
              colors.surface,
            )}
            role="listbox"
            aria-label={t('assignee', { defaultValue: 'Assignee' })}
          >
            {users.length > 8 && (
              <div className={cn('border-b p-2', colors.border)}>
                <input
                  type="search"
                  value={assigneeSearch}
                  onChange={(e) => setAssigneeSearch(e.target.value)}
                  placeholder={t('searchUsers', { defaultValue: 'Search users…' })}
                  className={cn(
                    'w-full rounded-lg border px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500/30',
                    colors.border,
                    colors.surface,
                    colors.text,
                  )}
                />
              </div>
            )}
            <ul className="max-h-56 overflow-y-auto py-1">
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={!hasAssignee}
                  onClick={() => void patchAssign('')}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                    !hasAssignee ? 'bg-indigo-50/80 dark:bg-indigo-950/30' : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
                    colors.text,
                  )}
                >
                  <AssigneeAvatar name={null} unassigned />
                  <span className="font-medium">{t('unassigned')}</span>
                </button>
              </li>
              {filteredUsers.map((u) => {
                const label = userLabel(u);
                const selected = assigneeId === String(u.id);
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => void patchAssign(String(u.id))}
                      className={cn(
                        'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                        selected ? 'bg-indigo-50/80 dark:bg-indigo-950/30' : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
                      )}
                    >
                      <AssigneeAvatar name={label} />
                      <div className="min-w-0">
                        <p className={cn('truncate font-medium', colors.text)}>{label}</p>
                        {u.userEmail && label !== u.userEmail && (
                          <p className={cn('truncate text-xs', colors.textMuted)}>{u.userEmail}</p>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
              {filteredUsers.length === 0 && (
                <li className={cn('px-3 py-4 text-center text-sm', colors.textMuted)}>
                  {t('noUsersFound', { defaultValue: 'No users found' })}
                </li>
              )}
            </ul>
          </div>
        </AnchoredMenu>
        )}
      </div>

      {/* Status */}
      <div className="flex min-w-0 flex-1 items-center gap-1.5 px-2 py-1">
        <span className={cn('hidden sm:inline text-[10px] font-semibold uppercase tracking-wider shrink-0', colors.textMuted)}>
          {t('status')}
        </span>

        {hasAssignee ? (
          <div
            role="group"
            aria-label={t('status')}
            className={cn(
              'inline-flex min-w-0 flex-1 items-center rounded-lg p-0.5',
              statusChip?.chipClass ?? colors.surfaceHover,
            )}
          >
            {STATUS_OPTIONS.map((opt) => {
              const isActive = status === opt.value;
              const chip = WORKFLOW_STATUS_CHIP[opt.value];
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => void patchStatus(opt.value)}
                  disabled={saving}
                  aria-pressed={isActive}
                  className={cn(
                    'inline-flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-medium transition-all sm:px-2.5 sm:text-xs',
                    'disabled:cursor-not-allowed',
                    isActive
                      ? 'bg-white text-slate-900 shadow-sm ring-1 ring-black/5 dark:bg-slate-900 dark:text-slate-100 dark:ring-white/10'
                      : cn(colors.textMuted, 'hover:text-current'),
                  )}
                >
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', chip.dotClass)} aria-hidden />
                  <span className="truncate">{t(opt.labelKey)}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <p className={cn('text-xs italic', colors.textMuted)} title={t('assignFirst')}>
            {t('assignFirst')}
          </p>
        )}

        {saving && (
          <Loader2 className={cn('h-3.5 w-3.5 shrink-0 animate-spin', colors.textMuted)} aria-label={t('saving')} />
        )}
      </div>
    </div>
  );
};

export default EmailWorkflowControls;
